/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useUserConsumer } from "~/context/User/User";
import { UserRole } from "~/models";
import { isNotEmpty } from "~/tools";
import { ACTION } from "./actions";
import { rules } from "./rules";

const onlySuperAdminActions = ["edit:employee"];

function validateAccess(userRole: UserRole, action: ACTION) {
  if (userRole === UserRole.SUPER_ADMIN) {
    return true;
  }

  if (
    userRole === UserRole.PRIVILEGED_USER ||
    userRole === UserRole.STANDARD_USER
  ) {
    return !onlySuperAdminActions.includes(action);
  }

  return (rules[userRole].static as ACTION[]).includes(action);
}

function mergeIntoArray<T>(input: T | T[]): T[] {
  return [...(Array.isArray(input) ? input : [input])];
}

type ActionInput = ACTION | ACTION[];

export const useAccess = (input: ActionInput | null = null) => {
  const { role } = useUserConsumer();

  const checkAccess = (action: ActionInput): boolean => {
    const mergedArr = mergeIntoArray(action);

    return (
      isNotEmpty(mergedArr) &&
      mergeIntoArray(action).every((act) => {
        return validateAccess(role, act);
      })
    );
  };

  return {
    checkAccess,
    hasAccess: input === null ? false : checkAccess(input),
  };
};

type CanProps = {
  action: ACTION;
  fallback?: React.ReactElement<unknown> | null;
  children: React.ReactElement<unknown>;
};

export const Can = ({ action, fallback = null, children }: CanProps) => {
  const { hasAccess } = useAccess(action);

  if (hasAccess) {
    return children;
  }

  return fallback;
};

export const isStandardUser = (userRole: UserRole): boolean => {
  return userRole === UserRole.STANDARD_USER || isPrivilegedUser(userRole);
};

export const isPrivilegedUser = (userRole: UserRole): boolean => {
  return userRole === UserRole.PRIVILEGED_USER || isSuperAdmin(userRole);
};

export const isSuperAdmin = (userRole: UserRole): boolean => {
  return userRole === UserRole.SUPER_ADMIN;
};
